import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'bootstrap/dist/css/bootstrap.min.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const IncidentReportByDate = ({ userEmail, userRole }) => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [userName, setUserName] = useState('All');
    const [users, setUsers] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (userRole === 'Admin') {
            fetchUsers();
        }
    }, [userRole]);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${BASE_URL}api/UserAccount/ListUsers`);
            setUsers(response.data);
        } catch (err) {
        }
    };

    const handleSearch = async () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let response;
            if (userRole === 'Admin') {
                response = await axios.get(
                    `${BASE_URL}api/Incidents/GetCustomerIncidentsAll`,
                    {
                        params: {
                            startDate,
                            endDate,
                            userName
                        }
                    }
                );
            } else {
                response = await axios.get(
                    `${BASE_URL}api/Incidents/GetUserCustomerIncidentsForUser`,
                    {
                        params: {
                            startDate,
                            endDate,
                            userEmail
                        }
                    }
                );
            }
            const sortedIncidents = response.data.sort((a, b) => 
                new Date(b.incidentDate) - new Date(a.incidentDate)
            );
            setIncidents(sortedIncidents);
        } catch (err) {
            setError('Failed to fetch incident reports');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        const headers = ['Date', 'Customer', 'Phone', 'Incident Type', 'Incident', 'Details', 'Document No', 'Created By'];
        const csvData = incidents.map(incident => [
            incident.incidentDate ? new Date(incident.incidentDate).toLocaleDateString() : 'N/A',
            incident.customerName || 'N/A',
            incident.customerPhone || 'N/A',
            incident.incidentType || 'N/A',
            incident.incidentText || 'N/A',
            incident.details || 'N/A',
            incident.documentNo || 'N/A',
            incident.createdBy || 'N/A'
        ]);
        
        const csv = [headers, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incident-report-${startDate}-to-${endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleExportText = () => {
        const displayUserName = userRole === 'Admin' ? userName : userEmail;
        const lines = [
            'Incident Report',
            `Date Range: ${startDate} to ${endDate}`,
            `User: ${displayUserName}`,
            ''
        ];

        incidents.forEach((incident, index) => {
            const date = incident.incidentDate ? new Date(incident.incidentDate).toLocaleDateString() : 'N/A';
            lines.push(`${index + 1}. ${date} - ${incident.customerName || 'N/A'}`);
            lines.push(`Phone: ${incident.customerPhone || 'N/A'}`);
            lines.push(`Incident Type: ${incident.incidentType || 'N/A'}`);
            lines.push(`Incident: ${incident.incidentText || 'N/A'}`);
            lines.push(`Details: ${incident.details || 'N/A'}`);
            lines.push(`Document No: ${incident.documentNo || 'N/A'}`);
            lines.push(`Created By: ${incident.createdBy || 'N/A'}`);
            lines.push('');
        });

        const text = lines.join('\n');

        const showCopied = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(showCopied).catch(err => {});
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            try {
                document.execCommand('copy');
                showCopied();
            } catch (err) {
            }
            document.body.removeChild(textarea);
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');
        
        doc.setFontSize(18);
        doc.text('Incident Report', 14, 15);
        
        doc.setFontSize(11);
        const displayUserName = userRole === 'Admin' ? userName : userEmail;
        doc.text(`Date Range: ${startDate} to ${endDate}`, 14, 25);
        doc.text(`User: ${displayUserName}`, 14, 32);
        
        const tableData = incidents.map(incident => [
            incident.incidentDate ? new Date(incident.incidentDate).toLocaleDateString() : 'N/A',
            incident.customerName || 'N/A',
            incident.customerPhone || 'N/A',
            incident.incidentType || 'N/A',
            incident.incidentText || 'N/A',
            incident.details || 'N/A',
            incident.documentNo || 'N/A',
            incident.createdBy || 'N/A'
        ]);

        autoTable(doc, {
            head: [['Date', 'Customer', 'Phone', 'Incident Type', 'Incident', 'Details', 'Document No', 'Created By']],
            body: tableData,
            startY: 38,
            styles: { fontSize: 10 },
            headStyles: { fillColor: [0, 0, 0] }
        });
        
        doc.save(`incident-report-${startDate}-to-${endDate}.pdf`);
    };

    return (
        <div className="container-fluid mt-3 px-4" style={{ maxWidth: '98%' }}>
            <div className="mb-3">
                <h2 className="fw-bold text-primary">Incident Report by Date</h2>
                <p className="text-muted mb-0">Generate and export incident reports based on date range and user</p>
            </div>
            
            <div className="card shadow-sm mb-3 border-0">
                <div className="card-header bg-primary text-white py-2">
                    <h6 className="mb-0"><i className="bi bi-funnel"></i> Filter Options</h6>
                </div>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">User</label>
                            {userRole === 'Admin' ? (
                                <select
                                    className="form-select"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                >
                                    <option value="All">All Users</option>
                                    {users.map((user, index) => (
                                        <option key={index} value={user.userName}>
                                            {user.userName}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={userEmail}
                                    disabled
                                />
                            )}
                        </div>
                    </div>
                    <div className="mt-3">
                        <button
                            className="btn btn-primary px-4"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Searching...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-search me-2"></i>
                                    Generate Report
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {incidents.length > 0 && (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white border-bottom">
                        <div className="d-flex justify-content-between align-items-center py-2">
                            <h6 className="mb-0 fw-bold text-dark">
                                <i className="bi bi-table me-2"></i>
                                Results
                            </h6>
                            <div>
                                <button className="btn btn-danger me-2" onClick={handleExportPDF}>
                                    <i className="bi bi-file-pdf me-2"></i>
                                    Export to PDF
                                </button>
                                <button className="btn btn-success me-2" onClick={handleExport}>
                                    <i className="bi bi-download me-2"></i>
                                    Export to CSV
                                </button>
                                <button className="btn btn-secondary" onClick={handleExportText}>
                                    <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-2`}></i>
                                    {copied ? 'Copied!' : 'Copy as Text'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover table-striped mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-semibold">Date</th>
                                        <th className="fw-semibold">Customer</th>
                                        <th className="fw-semibold">Phone</th>
                                        <th className="fw-semibold">Incident Type</th>
                                        <th className="fw-semibold">Incident</th>
                                        <th className="fw-semibold">Details</th>
                                        <th className="fw-semibold">Document No</th>
                                        <th className="fw-semibold">Created By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {incidents.map((incident, index) => (
                                        <tr key={index}>
                                            <td>{incident.incidentDate ? new Date(incident.incidentDate).toLocaleDateString() : 'N/A'}</td>
                                            <td>
                                                {incident.customerName ? (
                                                    <span 
                                                        style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                                                        onClick={() => navigate(`/customer-view/${incident.customerId}`)}
                                                    >
                                                        {incident.customerName}
                                                    </span>
                                                ) : 'N/A'}
                                            </td>
                                            <td>{incident.customerPhone || 'N/A'}</td>
                                            <td>{incident.incidentType || 'N/A'}</td>
                                            <td>{incident.incidentText || 'N/A'}</td>
                                            <td>{incident.details || 'N/A'}</td>
                                            <td>
                                                <span 
                                                    style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => {
                                                        if (incident.documentNo) {
                                                            navigate(`/details/${incident.documentNo}`);
                                                        } else {
                                                            setShowModal(true);
                                                        }
                                                    }}
                                                >
                                                    {incident.documentNo || 'N/A'}
                                                </span>
                                            </td>
                                            <td>{incident.createdBy || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>No Report Available</Modal.Title>
                </Modal.Header>
                <Modal.Body>No report available for this incident.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowModal(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default IncidentReportByDate;
